import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { UserProfile, Gelanggang, BengkungLevel } from '../../types';
import { getMembersDB, saveMemberDB, saveInvoiceDB } from '../../services/db';
import { sanitizeIc, sanitizePhone, findDuplicateIcMember, findDuplicatePhoneMember, toTitleCase } from '../../utils/formatters';
import { createOnboardingInvoice, UNIFORM_SIZES, getUniformPriceBySize } from '../../utils/pricing';
import { SEO } from '../../components/common/SEO';
import { UniformSizeChartModal } from '../../components/common/UniformSizeChartModal';
import {
  Users,
  UserPlus,
  Link2,
  LogOut,
  CheckCircle2,
  AlertCircle,
  Ruler,
  ArrowRight,
  Plus,
  Trash2
} from 'lucide-react';

interface WarisChildRequiredPageProps {
  onChildAdded: () => void;
}

interface ChildRegistrationItem {
  id: string;
  name: string;
  icNo: string;
  phone: string;
  gelanggang: Gelanggang;
  uniformSize: string;
  relationship: string;
}

interface LinkChildItem {
  id: string;
  icNo: string;
}

export const WarisChildRequiredPage: React.FC<WarisChildRequiredPageProps> = ({ onChildAdded }) => {
  const { currentUser, logout } = useAuth();
  const { showToast } = useToast();

  const [activeMode, setActiveMode] = useState<'register' | 'link'>('register');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSizeChartOpen, setIsSizeChartOpen] = useState(false);

  const defaultGelanggang: Gelanggang =
    currentUser?.gelanggang && currentUser.gelanggang !== 'Tiada' && currentUser.gelanggang !== 'Semua Gelanggang'
      ? currentUser.gelanggang
      : 'Pusaka Warisan';

  // Form state: Register New Pesilat (supports multiple pesilat: anak, isteri, suami, etc.)
  const [children, setChildren] = useState<ChildRegistrationItem[]>([
    {
      id: 'pesilat_1',
      name: '',
      icNo: '',
      phone: '',
      gelanggang: defaultGelanggang,
      uniformSize: '130cm',
      relationship: 'Anak'
    }
  ]);

  // Form state: Link Existing Pesilat (supports multiple pesilat)
  const [linkChildren, setLinkChildren] = useState<LinkChildItem[]>([
    {
      id: 'link_1',
      icNo: ''
    }
  ]);

  const handleAddChild = () => {
    setChildren(prev => [
      ...prev,
      {
        id: `pesilat_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
        name: '',
        icNo: '',
        phone: '',
        gelanggang: prev[0]?.gelanggang || defaultGelanggang,
        uniformSize: '130cm',
        relationship: 'Anak'
      }
    ]);
  };

  const handleRemoveChild = (id: string) => {
    if (children.length <= 1) {
      showToast('warning', 'Wajib Diisi', 'Pendaftaran sebagai Waris memerlukan sekurang-kurangnya seorang pesilat berdaftar.');
      return;
    }
    setChildren(prev => prev.filter(c => c.id !== id));
  };

  const handleUpdateChild = (id: string, field: keyof ChildRegistrationItem, value: any) => {
    setChildren(prev =>
      prev.map(c => (c.id === id ? { ...c, [field]: value } : c))
    );
  };

  const handleAddLinkChild = () => {
    setLinkChildren(prev => [
      ...prev,
      {
        id: `link_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
        icNo: ''
      }
    ]);
  };

  const handleRemoveLinkChild = (id: string) => {
    if (linkChildren.length <= 1) return;
    setLinkChildren(prev => prev.filter(l => l.id !== id));
  };

  const handleUpdateLinkChild = (id: string, icNo: string) => {
    setLinkChildren(prev =>
      prev.map(l => (l.id === id ? { ...l, icNo } : l))
    );
  };

  const handleRegisterNewChild = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    const parentIc = sanitizeIc(currentUser.icNo || '');
    const parentPhone = sanitizePhone(currentUser.phone || '');

    // 1. Validation across all members in the form
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      const label = children.length > 1 ? `Pesilat #${i + 1}` : 'Pesilat';
      const cleanName = child.name.trim();
      const cleanIc = sanitizeIc(child.icNo);
      const cleanPhone = sanitizePhone(child.phone);

      if (!cleanName || cleanName.length < 2) {
        showToast('error', 'Nama Diperlukan', `Sila masukkan nama penuh untuk ${label}.`);
        return;
      }

      if (!cleanIc) {
        showToast('error', 'No. IC Tidak Sah', `Sila masukkan No. Kad Pengenalan / MyKid yang sah untuk ${label}.`);
        return;
      }

      if (cleanIc === parentIc) {
        showToast('error', 'No. IC Bertindih', `No. Kad Pengenalan untuk ${label} tidak boleh sama dengan No. IC Waris.`);
        return;
      }

      if (cleanPhone && parentPhone && cleanPhone === parentPhone) {
        showToast('error', 'No. Telefon Bertindih', `No. Telefon untuk ${label} tidak boleh sama dengan No. Telefon Waris. Kosongkan jika pesilat belum mempunyai telefon sendiri.`);
        return;
      }
    }

    // 2. Intra-list duplicates check
    const cleanIcs = children.map(c => sanitizeIc(c.icNo));
    const seenIcs = new Set<string>();
    for (let i = 0; i < cleanIcs.length; i++) {
      const ic = cleanIcs[i];
      if (seenIcs.has(ic)) {
        showToast('error', 'No. IC Bertindih', `Terdapat No. IC yang sama berulang dalam senarai pesilat yang dimasukkan (${ic}).`);
        return;
      }
      seenIcs.add(ic);
    }

    const seenPhones = new Set<string>();
    for (let i = 0; i < children.length; i++) {
      const phone = sanitizePhone(children[i].phone);
      if (phone) {
        if (seenPhones.has(phone)) {
          showToast('error', 'No. Telefon Bertindih', `No. telefon (${phone}) berulang untuk lebih daripada seorang pesilat dalam senarai.`);
          return;
        }
        seenPhones.add(phone);
      }
    }

    setIsSubmitting(true);
    try {
      const existing = await getMembersDB();

      // Check for conflicts in existing members
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        const cleanIc = sanitizeIc(child.icNo);
        const cleanPhone = sanitizePhone(child.phone);
        const label = children.length > 1 ? `Pesilat #${i + 1} (${child.name})` : `Pesilat ${child.name}`;

        const duplicate = findDuplicateIcMember(cleanIc, undefined, existing);
        if (duplicate) {
          if (duplicate.parentId && duplicate.parentId.trim() && duplicate.parentId.trim() !== currentUser.id) {
            showToast('error', 'Pesilat Sudah Berdaftar', `${label} (${cleanIc}) sudah berdaftar di bawah akaun waris lain.`);
            setIsSubmitting(false);
            return;
          }
        }

        if (cleanPhone) {
          const duplicatePhone = findDuplicatePhoneMember(cleanPhone, duplicate?.id, existing);
          if (duplicatePhone) {
            showToast('error', 'No. Telefon Bertindih', `No. telefon (${cleanPhone}) bagi ${label} telah digunakan oleh ahli lain (${duplicatePhone.name} - ${duplicatePhone.id}). Kosongkan jika berkongsi talian waris.`);
            setIsSubmitting(false);
            return;
          }
        }
      }

      // Calculate sequential IDs
      let maxNum = 0;
      for (const m of existing) {
        const match = m.id.match(/^asmm-(\d+)$/i);
        if (match) {
          const num = parseInt(match[1], 10);
          if (num > maxNum) maxNum = num;
        }
      }

      const savedNames: string[] = [];

      for (const child of children) {
        const cleanName = toTitleCase(child.name.trim());
        const cleanIc = sanitizeIc(child.icNo);
        const childCleanPhone = sanitizePhone(child.phone) || '';
        const childCleanEmail = `${cleanIc}@asmm.org.my`;

        const existingChild = existing.find(m => m.icNo && sanitizeIc(m.icNo) === cleanIc);

        if (existingChild) {
          // Link existing member
          const updatedChild: UserProfile = {
            ...existingChild,
            parentId: currentUser.id,
            relationship: child.relationship || 'Tanggungan',
            kinName: currentUser.name,
            kinPhone: currentUser.phone
          };
          await saveMemberDB(updatedChild);
          savedNames.push(existingChild.name);
        } else {
          // New member profile
          maxNum += 1;
          const childId = `ASMM-${String(maxNum).padStart(3, '0')}`;

          const newChild: UserProfile = {
            id: childId,
            name: cleanName,
            icNo: cleanIc,
            email: childCleanEmail,
            phone: childCleanPhone,
            address: currentUser.address || '',
            role: 'Ahli',
            roles: ['Ahli'],
            gelanggang: child.gelanggang,
            bengkung: 'Putih' as BengkungLevel,
            bengkungBadge: '/badges/putih0.png',
            photoUrl: '/default-avatar.svg',
            joinedDate: new Date().toISOString().split('T')[0],
            status: 'Menunggu Pengesahan',
            parentId: currentUser.id,
            relationship: child.relationship || 'Tanggungan',
            password: cleanIc,
            sessionTime: 'Semua Sesi',
            authProvider: 'password',
            isOldMember: false,
            onboardingStatus: 'pending_payment',
            uniformSize: child.uniformSize,
            kinName: currentUser.name,
            kinPhone: currentUser.phone
          };

          const childOnbInv = createOnboardingInvoice(newChild, child.uniformSize);
          await saveInvoiceDB(childOnbInv);
          newChild.onboardingInvoiceId = childOnbInv.id;

          await saveMemberDB(newChild);
          savedNames.push(cleanName);
        }
      }

      window.dispatchEvent(new Event('asmm_db_updated'));

      showToast(
        'success',
        'Pesilat Berjaya Didaftarkan',
        `${savedNames.length} orang pesilat (${savedNames.join(', ')}) berjaya didaftarkan. Sila selesaikan bayaran pendaftaran untuk pengesahan pentadbir.`
      );
      onChildAdded();
    } catch {
      showToast('error', 'Ralat Pendaftaran', 'Gagal mendaftarkan pesilat. Sila cuba sebentar lagi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLinkExistingChild = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    const parentIc = sanitizeIc(currentUser.icNo || '');

    // Validate link members
    for (let i = 0; i < linkChildren.length; i++) {
      const item = linkChildren[i];
      const cleanIc = sanitizeIc(item.icNo);
      const label = linkChildren.length > 1 ? `Pesilat #${i + 1}` : 'Pesilat';

      if (!cleanIc) {
        showToast('error', 'No. IC Diperlukan', `Sila masukkan No. Kad Pengenalan / MyKid untuk ${label}.`);
        return;
      }

      if (cleanIc === parentIc) {
        showToast('error', 'No. IC Sama', `No. Kad Pengenalan untuk ${label} tidak boleh sama dengan No. IC Waris.`);
        return;
      }
    }

    // Intra-list duplicates check
    const cleanIcs = linkChildren.map(l => sanitizeIc(l.icNo));
    const seen = new Set<string>();
    for (const ic of cleanIcs) {
      if (seen.has(ic)) {
        showToast('error', 'No. IC Bertindih', `Terdapat No. IC yang sama berulang dalam senarai pautan (${ic}).`);
        return;
      }
      seen.add(ic);
    }

    setIsSubmitting(true);
    try {
      const existing = await getMembersDB();
      const childrenToLink: UserProfile[] = [];

      for (let i = 0; i < linkChildren.length; i++) {
        const item = linkChildren[i];
        const cleanIc = sanitizeIc(item.icNo);
        const label = linkChildren.length > 1 ? `Pesilat #${i + 1} (${cleanIc})` : `Pesilat (${cleanIc})`;

        const child = existing.find(m => m.icNo && sanitizeIc(m.icNo) === cleanIc);
        if (!child) {
          showToast(
            'error',
            'Pesilat Tidak Ditemui',
            `${label} belum wujud dalam pangkalan data. Sila gunakan tab "Daftar Pesilat Baharu" untuk pendaftaran baharu.`
          );
          setIsSubmitting(false);
          return;
        }

        if (child.parentId && child.parentId.trim() && child.parentId.trim() !== currentUser.id) {
          showToast('error', 'Pesilat Sudah Dipautkan', `Pesilat ${child.name} (${cleanIc}) sudah dipautkan ke akaun waris lain.`);
          setIsSubmitting(false);
          return;
        }

        childrenToLink.push(child);
      }

      // Perform linking
      const linkedNames: string[] = [];
      for (const child of childrenToLink) {
        const updatedChild: UserProfile = {
          ...child,
          parentId: currentUser.id,
          relationship: child.relationship || 'Tanggungan',
          kinName: currentUser.name,
          kinPhone: currentUser.phone
        };
        await saveMemberDB(updatedChild);
        linkedNames.push(child.name);
      }

      window.dispatchEvent(new Event('asmm_db_updated'));

      showToast('success', 'Pautan Berjaya!', `${linkedNames.length} orang pesilat (${linkedNames.join(', ')}) berjaya dipautkan.`);
      onChildAdded();
    } catch {
      showToast('error', 'Ralat Pautan', 'Gagal memautkan pesilat.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 selection:bg-asmm-red selection:text-white flex flex-col justify-between py-6 px-4 sm:px-6 lg:px-8">
      <SEO
        title="Daftar Pesilat Tanggungan | Portal Waris ASMM"
        description="Pendaftaran pesilat tanggungan atau ahli keluarga adalah wajib bagi akaun Waris sebelum boleh mengakses papan pemuka ASMM."
      />

      {/* Main Container */}
      <div className="max-w-3xl w-full mx-auto space-y-6 animate-fade-in">
        {/* Top Hero Banner */}
        <div className="bg-slate-900 rounded-3xl p-6 sm:p-8 text-white shadow-xl relative overflow-hidden border border-slate-800">
          <div className="absolute right-0 top-0 translate-x-8 -translate-y-8 w-44 h-44 bg-asmm-red/30 rounded-full blur-3xl pointer-events-none" />

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 relative z-10">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-asmm-red/20 border border-asmm-red/40 flex items-center justify-center shrink-0 text-white shadow-inner">
                <Users className="w-7 h-7 text-red-500" />
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-black tracking-wider uppercase bg-asmm-red text-white px-2.5 py-0.5 rounded-full">
                    Akses Terhad • Portal Waris
                  </span>
                  <span className="text-[10px] font-bold text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded-full border border-amber-400/20">
                    Sekurang-kurangnya 1 Pesilat Berdaftar
                  </span>
                </div>
                <h1 className="text-xl sm:text-2xl font-black tracking-tight text-white">
                  Pendaftaran Pesilat Tanggungan Diperlukan
                </h1>
                <p className="text-xs text-slate-300 max-w-lg leading-relaxed">
                  Selamat datang, <strong className="text-white font-semibold">{currentUser?.name}</strong>. Sebagai Waris / Penjaga, anda boleh mendaftarkan seorang atau lebih pesilat (anak, isteri, suami, atau ahli keluarga) untuk membuka akses Dashboard portal ASMM.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={logout}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-xs font-bold transition-all border border-slate-700 self-start sm:self-center shrink-0 cursor-pointer"
              title="Log Keluar dari akaun ini"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Log Keluar</span>
            </button>
          </div>
        </div>

        {/* Informative Explanation Banner */}
        <div className="p-4 bg-amber-50/70 border border-amber-200/90 rounded-2xl flex items-start gap-3 shadow-2xs">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900 leading-relaxed">
            <strong className="font-bold">Mengapa butiran pesilat diperlukan?</strong> Akaun portal Waris berfungsi untuk memantau silibus latihan, kehadiran gelanggang, dan menguruskan yuran bulanan ahli keluarga atau tanggungan anda (seperti anak, isteri, atau suami). Anda boleh mendaftarkan lebih daripada seorang pesilat serentak di borang ini.
          </div>
        </div>

        {/* Mode Selector Tabs */}
        <div className="grid grid-cols-2 gap-2 p-1.5 bg-slate-100 rounded-2xl border border-slate-200 shadow-2xs">
          <button
            type="button"
            onClick={() => setActiveMode('register')}
            className={`py-2.5 px-3 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-2 cursor-pointer border-none ${activeMode === 'register'
              ? 'bg-white text-asmm-red shadow-sm'
              : 'text-slate-600 hover:text-slate-900'
              }`}
          >
            <UserPlus className="w-4 h-4" />
            <span>1. Daftar Pesilat Baharu ({children.length})</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveMode('link')}
            className={`py-2.5 px-3 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-2 cursor-pointer border-none ${activeMode === 'link'
              ? 'bg-white text-asmm-red shadow-sm'
              : 'text-slate-600 hover:text-slate-900'
              }`}
          >
            <Link2 className="w-4 h-4" />
            <span>2. Pautkan Pesilat Sedia Ada ({linkChildren.length})</span>
          </button>
        </div>

        {/* MODE 1: REGISTER NEW PESILAT FORM */}
        {activeMode === 'register' && (
          <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm p-6 sm:p-8 space-y-5 animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
              <div>
                <h2 className="text-sm font-black text-slate-900">
                  Borang Pendaftaran Pesilat Baharu
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Isi butiran pesilat yang anda daftarkan (anak, isteri, suami, atau ahli keluarga) untuk pendaftaran latihan dan saiz uniform silat.
                </p>
              </div>
              <span className="text-[11px] font-black bg-red-50 text-asmm-red px-2.5 py-1 rounded-full border border-red-100 self-start sm:self-center">
                {children.length} Orang Pesilat
              </span>
            </div>

            <form onSubmit={handleRegisterNewChild} className="space-y-5">
              {/* Dynamic Pesilat Cards List */}
              <div className="space-y-4">
                {children.map((child, idx) => (
                  <div
                    key={child.id}
                    className="p-4 sm:p-5 bg-slate-50/70 rounded-2xl border border-slate-200 shadow-2xs space-y-3.5 transition-all"
                  >
                    <div className="flex items-center justify-between border-b border-slate-200/80 pb-2.5">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-slate-900 text-white text-[11px] font-black flex items-center justify-center">
                          {idx + 1}
                        </span>
                        <span className="text-xs font-black text-slate-900">
                          {children.length > 1 ? `Pesilat #${idx + 1}` : 'Butiran Pesilat'}
                        </span>
                        {idx === 0 && (
                          <span className="text-[9px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-md">
                            Pesilat Utama
                          </span>
                        )}
                      </div>

                      {children.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveChild(child.id)}
                          className="text-xs text-red-600 hover:text-red-700 font-bold flex items-center gap-1 transition-colors border-none bg-transparent cursor-pointer"
                          title="Padam maklumat pesilat ini"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span>Padam</span>
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-bold text-slate-700 mb-1">
                          Nama Penuh Pesilat *
                        </label>
                        <input
                          type="text"
                          required
                          value={child.name}
                          onChange={(e) => handleUpdateChild(child.id, 'name', e.target.value)}
                          placeholder="Contoh: Siti Nurhaliza / Muhammad Adam"
                          className="w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">
                          Hubungan dengan Waris *
                        </label>
                        <select
                          value={child.relationship}
                          onChange={(e) => handleUpdateChild(child.id, 'relationship', e.target.value)}
                          className="w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium transition-all"
                        >
                          <option value="Anak">Anak</option>
                          <option value="Isteri">Isteri</option>
                          <option value="Suami">Suami</option>
                          <option value="Adik-beradik">Adik-beradik</option>
                          <option value="Ibu">Ibu</option>
                          <option value="Bapa">Bapa</option>
                          <option value="Tanggungan Lain">Tanggungan Lain</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">
                          No. Kad Pengenalan / MyKid Pesilat *
                        </label>
                        <input
                          type="text"
                          required
                          value={child.icNo}
                          onChange={(e) => handleUpdateChild(child.id, 'icNo', e.target.value)}
                          placeholder="Contoh: 950812-02-5432"
                          className="w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">
                          Gelanggang Latihan Pesilat *
                        </label>
                        <select
                          value={child.gelanggang}
                          onChange={(e) => handleUpdateChild(child.id, 'gelanggang', e.target.value as Gelanggang)}
                          className="w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium transition-all"
                        >
                          <option value="Pusaka Warisan">Pusaka Warisan</option>
                          <option value="Wadi AnNour">Wadi AnNour</option>
                          <option value="Paya Besar">Paya Besar</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                            <span>Saiz Uniform Silat Pesilat *</span>
                          </label>
                          <button
                            type="button"
                            onClick={() => setIsSizeChartOpen(true)}
                            className="text-blue-600 hover:text-blue-800 underline inline-flex items-center gap-1 text-[11px] font-bold cursor-pointer bg-transparent border-none p-0"
                            title="Buka Carta Cadangan Saiz"
                          >
                            <Ruler className="w-3 h-3" />
                            <span>Carta Saiz</span>
                          </button>
                        </div>
                        <select
                          value={child.uniformSize}
                          onChange={(e) => handleUpdateChild(child.id, 'uniformSize', e.target.value)}
                          className="w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium transition-all"
                        >
                          {UNIFORM_SIZES.map(s => (
                            <option key={s} value={s}>
                              {s} — RM {getUniformPriceBySize(s)} ({parseInt(s) <= 130 ? 'Kanak-kanak' : parseInt(s) <= 170 ? 'Remaja / Dewasa' : 'Tinggi / Dewasa'})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">
                          No. Telefon Pesilat (Pilihan)
                        </label>
                        <input
                          type="text"
                          value={child.phone}
                          onChange={(e) => handleUpdateChild(child.id, 'phone', e.target.value)}
                          placeholder="Kosongkan jika guna nombor waris"
                          className="w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium transition-all"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Add Another Pesilat Button */}
              <button
                type="button"
                onClick={handleAddChild}
                className="w-full py-3 px-4 bg-white hover:bg-slate-50 text-slate-800 hover:text-slate-900 rounded-2xl border-2 border-dashed border-slate-300 hover:border-asmm-red text-xs font-extrabold flex items-center justify-center gap-2 transition-all cursor-pointer shadow-2xs group"
              >
                <div className="w-6 h-6 rounded-full bg-red-50 text-asmm-red flex items-center justify-center group-hover:bg-asmm-red group-hover:text-white transition-all">
                  <Plus className="w-3.5 h-3.5" />
                </div>
                <span>+ Tambah Pesilat Lagi (Jika Ada Lebih 1 Orang)</span>
              </button>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3.5 bg-asmm-red hover:bg-asmm-crimson text-white font-bold text-xs rounded-xl transition-all shadow-sm flex items-center justify-center gap-2 disabled:opacity-60 border-none cursor-pointer"
                >
                  {isSubmitting ? (
                    <span>Menyimpan Rekod Pesilat...</span>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      <span>
                        Daftar {children.length > 1 ? `${children.length} Orang Pesilat` : 'Pesilat'} & Teruskan
                      </span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* MODE 2: LINK EXISTING PESILAT FORM */}
        {activeMode === 'link' && (
          <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm p-6 sm:p-8 space-y-5 animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
              <div>
                <h2 className="text-sm font-black text-slate-900">
                  Pautkan Pesilat yang Telah Berdaftar
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Sekiranya pesilat (anak, isteri, suami, atau ahli keluarga) sudah didaftarkan terlebih dahulu oleh jurulatih atau pentadbir gelanggang, masukkan No. Kad Pengenalan / MyKid pesilat untuk memautkan akaun.
                </p>
              </div>
              <span className="text-[11px] font-black bg-slate-100 text-slate-700 px-2.5 py-1 rounded-full border border-slate-200 self-start sm:self-center">
                {linkChildren.length} Pautan Pesilat
              </span>
            </div>

            <form onSubmit={handleLinkExistingChild} className="space-y-4">
              <div className="space-y-3">
                {linkChildren.map((item, idx) => (
                  <div key={item.id} className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-slate-700">
                        {linkChildren.length > 1 ? `No. Kad Pengenalan / MyKid Pesilat #${idx + 1} *` : 'No. Kad Pengenalan / MyKid Pesilat *'}
                      </span>
                      {linkChildren.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveLinkChild(item.id)}
                          className="text-xs text-red-600 hover:text-red-700 font-bold flex items-center gap-1 border-none bg-transparent cursor-pointer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span>Padam</span>
                        </button>
                      )}
                    </div>
                    <input
                      type="text"
                      required
                      value={item.icNo}
                      onChange={(e) => handleUpdateLinkChild(item.id, e.target.value)}
                      placeholder="Contoh: 950812-02-5432 atau 120504-02-1234"
                      className="w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium transition-all"
                    />
                  </div>
                ))}
              </div>

              {/* Add Another Link Pesilat Button */}
              <button
                type="button"
                onClick={handleAddLinkChild}
                className="w-full py-2.5 px-3 bg-white hover:bg-slate-50 text-slate-700 rounded-xl border border-dashed border-slate-300 text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer shadow-2xs"
              >
                <Plus className="w-3.5 h-3.5 text-asmm-red" />
                <span>+ Pautkan No. IC Pesilat Sedia Ada Lagi</span>
              </button>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3.5 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl transition-all shadow-sm flex items-center justify-center gap-2 disabled:opacity-60 border-none cursor-pointer"
                >
                  {isSubmitting ? (
                    <span>Memautkan Pesilat...</span>
                  ) : (
                    <>
                      <Link2 className="w-4 h-4" />
                      <span>
                        Pautkan {linkChildren.length > 1 ? `${linkChildren.length} Orang Pesilat` : 'Pesilat'} & Teruskan
                      </span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Uniform Size Chart Modal */}
      <UniformSizeChartModal
        isOpen={isSizeChartOpen}
        onClose={() => setIsSizeChartOpen(false)}
      />

      {/* Clean Footer note */}
      <div className="text-center text-[11px] text-slate-400 py-4">
        Persatuan Seni Silat Awang Sulung Merah Muda • Portal Pengurusan Keluarga & Waris
      </div>
    </div>
  );
};
